/**
 * COMPOSANT   : LanguageSwitcher
 * OBJECTIF    : Sélecteur de langue globalement accessible (header, mobile).
 *               Change la langue de l'app en cascade (i18n + PDF query).
 * ARCHITECTURE: Consomme /api/i18n/languages. Persistance localStorage.
 * API PROPS   : className?, testId?, variant? ('inline'|'compact')
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function LanguageSwitcher({ className = "", testId = "language-switcher", variant = "compact" }) {
  const { i18n, t } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/i18n/languages`)
      .then((r) => setLanguages(r.data.languages || []))
      .catch(() => setLanguages([
        { code: "fr", label: "Français", flag: "🇫🇷" },
        { code: "en", label: "English",   flag: "🇬🇧" },
        { code: "pt", label: "Português", flag: "🇵🇹" },
        { code: "ar", label: "العربية",  flag: "🇸🇦" },
      ]));
  }, []);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  const pick = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  if (variant === "inline") {
    return (
      <div className={`flex flex-wrap gap-2 ${className}`} data-testid={testId}>
        {languages.map((l) => (
          <button
            key={l.code}
            onClick={() => pick(l.code)}
            data-testid={`lang-${l.code}`}
            className={`px-3 py-2 rounded-full text-sm font-medium border transition min-h-[40px] ${
              i18n.language === l.code
                ? "bg-primary text-white border-primary"
                : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
            }`}
          >
            <span className="mr-1.5">{l.flag}</span>{l.native || l.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`${testId}-toggle`}
        aria-label={t("ui.language")}
        className="min-h-[40px] px-3 py-2 rounded-full inline-flex items-center gap-2 border border-slate-200 bg-white hover:border-slate-300 text-sm"
      >
        <Globe className="w-4 h-4 text-primary" />
        <span className="font-medium text-slate-700">{current?.flag}</span>
        <span className="uppercase text-xs text-slate-500 tracking-wider">{i18n.language}</span>
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-full mt-1 w-56 rounded-xl bg-white border border-slate-200 shadow-lg z-50 overflow-hidden">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => pick(l.code)}
                data-testid={`lang-${l.code}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-slate-50 transition ${
                  i18n.language === l.code ? "bg-primary/5" : ""
                }`}
              >
                <span className="text-lg">{l.flag}</span>
                <span className="flex-1 text-left">
                  <div className="font-medium text-ink">{l.native || l.label}</div>
                  <div className="text-xs text-slate-500 uppercase">{l.code}</div>
                </span>
                {i18n.language === l.code && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
